import type { MutableRefObject, ReactNode } from "react";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export interface MonthCalendarCell {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
}

interface MonthCalendarProps {
  month: Date;
  renderCell: (cell: MonthCalendarCell) => ReactNode;
  /** When set, the cell for the calendar “today” (local date) assigns into this ref. */
  todayCellRef?: MutableRefObject<HTMLDivElement | null>;
}

function startOfWeekMon(d: Date): Date {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const result = new Date(d);
  result.setDate(d.getDate() + diff);
  result.setHours(0, 0, 0, 0);
  return result;
}

function isSameDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function buildMonthGrid(month: Date): MonthCalendarCell[] {
  const firstOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = startOfWeekMon(firstOfMonth);
  const today = new Date();
  const cells: MonthCalendarCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    cells.push({
      date,
      isCurrentMonth: date.getMonth() === month.getMonth(),
      isToday: isSameDate(date, today),
    });
  }
  return cells;
}

export function MonthCalendar({ month, renderCell, todayCellRef }: MonthCalendarProps) {
  const cells = buildMonthGrid(month);
  return (
    <div className="month-calendar">
      <div className="month-calendar-grid month-calendar-headrow">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="month-calendar-head">
            {label}
          </div>
        ))}
      </div>
      <div className="month-calendar-grid">
        {cells.map((cell) => (
          <div
            key={cell.date.toISOString()}
            ref={
              cell.isToday && todayCellRef
                ? (el) => {
                    todayCellRef.current = el;
                  }
                : undefined
            }
            className={[
              "month-calendar-cell",
              cell.isCurrentMonth ? "" : "is-out-of-month",
              cell.isToday ? "is-today" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {renderCell(cell)}
          </div>
        ))}
      </div>
    </div>
  );
}
